import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckIcon,
  ChevronDownIcon,
  CircleIcon,
  Component1Icon,
  DimensionsIcon,
  HomeIcon,
  RowsIcon,
  SewingPinIcon,
  StackIcon,
  TokensIcon,
  ValueIcon,
} from '@radix-ui/react-icons';
import { UnstyledButton } from '@/shared/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/components/shadcn/dropdown-menu';
import { GROUP_DIMENSIONS, type GroupBy } from '../../pages/property-grouping';

/** The ordered options the menu lists, each with its spec-mandated icon. */
const OPTIONS: { value: GroupBy; Icon: React.ComponentType<{ width?: number; height?: number }> }[] = [
  { value: 'none', Icon: RowsIcon },
  { value: 'city', Icon: SewingPinIcon },
  { value: 'neighborhood', Icon: HomeIcon },
  { value: 'category', Icon: Component1Icon },
  { value: 'business', Icon: TokensIcon },
  { value: 'status', Icon: CircleIcon },
  { value: 'total_area', Icon: DimensionsIcon },
  { value: 'constructed_area', Icon: DimensionsIcon },
  { value: 'sale_price', Icon: ValueIcon },
  { value: 'rental_price', Icon: ValueIcon },
];

interface PageHeaderGroupByMenuProps {
  /** The active grouping dimension. */
  group: GroupBy;
  /** Called with the chosen dimension when a menu option is selected. */
  onChange: (next: GroupBy) => void;
}

/**
 * The "Agrupar" dropdown that sits after the Filtros button in the search row.
 *
 * The trigger mirrors the Filtros button exactly — same padding, radius, font
 * and the `--selection-active` / `--seed-base` active treatment — so the two
 * read as one control family. It is built on the repo's shadcn dropdown so the
 * popover styling (radius, surface, shadow) and keyboard/outside-click
 * behaviour come for free.
 *
 * @param props - The active group and the change handler.
 * @returns The grouping dropdown control.
 */
export const PageHeaderGroupByMenu: React.FC<PageHeaderGroupByMenuProps> = ({ group, onChange }) => {
  const { t } = useTranslation('features/properties');
  const isActive = group !== 'none';
  const activeLabel = t(`list.grouping.options.${group}`);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <UnstyledButton
          type="button"
          data-testid="group-by-button"
          aria-label={isActive ? t('listHeader.groupByActive', { label: activeLabel }) : t('listHeader.groupBy')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-medium)' as unknown as number,
            background: isActive ? 'var(--selection-active)' : 'transparent',
            color: isActive ? 'var(--seed-base)' : 'var(--text-secondary)',
            border: isActive ? 'none' : '1px solid var(--border-medium)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            whiteSpace: 'nowrap',
          }}
        >
          <StackIcon width={14} height={14} />
          {isActive ? t('listHeader.groupByActive', { label: activeLabel }) : t('listHeader.groupBy')}
          <ChevronDownIcon width={14} height={14} />
        </UnstyledButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuLabel
          className="font-(--font-semibold)"
          style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)' }}
        >
          {t('listHeader.groupByMenuTitle')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map(({ value, Icon }, index) => {
          const selected = value === group;
          return (
            <React.Fragment key={value}>
              {/* The "Sem agrupamento" reset reads as its own choice, set off
                  from the real dimensions by a rule. */}
              {index === 1 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                data-testid={`group-by-option-${value}`}
                aria-checked={selected}
                role="menuitemradio"
                onSelect={() => onChange(value)}
                style={{
                  color: selected ? 'var(--seed-base)' : 'var(--text-primary)',
                  fontWeight: selected
                    ? ('var(--font-semibold)' as unknown as number)
                    : ('var(--font-medium)' as unknown as number),
                }}
              >
                <Icon width={15} height={15} />
                <span style={{ flex: 1 }}>{t(`list.grouping.options.${value}`)}</span>
                {selected && <CheckIcon width={15} height={15} />}
              </DropdownMenuItem>
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
